import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import {
  Flex,
  FormControl,
  FormLabel,
  Input,
  ListItem,
  Text,
  Textarea,
  UnorderedList,
  useToast,
} from '@chakra-ui/react';
import { DOCUMENT_ACCEPTED_FORMATS, EXPIRTY_DATE_REGEX } from 'modules/kyc/constants/constants';
import { INPUT_FILE_FORMATS } from 'modules/merchantinfo/constants/constants';
import { showError } from 'utils/notifications';
import { formatString, snakeToTitleCase } from 'utils/utils';
import FileUploader from 'components/KycRfi/FileUploader';

const KycRfiResponse = ({ rfi, documentInfo, questionNo, onAnswerChange }) => {
  const {
    id,
    prompt_format: { prompt, is_document_required, document_sub_type, document_type, is_front_back_required },
  } = rfi;

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [answer, setAnswer] = useState({
    notes: '',
    document: [],
  });
  const [documentDetails, setDocumentDetails] = useState({
    document_type: document_type,
    document_sub_type: '',
    expiry_date: '',
    comment: '',
    metadata: '',
  });

  const toast = useToast();

  const updateAnswer = useCallback(
    (newAnswer) => {
      setAnswer(newAnswer);
      onAnswerChange?.(id, newAnswer.notes, newAnswer.document);
    },
    [id, onAnswerChange]
  );

  const handleTextareaChange = (e, field) => {
    const value = e.target.value;

    if (field === 'notes') {
      updateAnswer({ ...answer, notes: value });
    } else if (field === 'comment') {
      setDocumentDetails({ ...documentDetails, comment: value });
    }
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value;

    if (field === 'expiry_date') {
      if (value?.length === 7 && !EXPIRTY_DATE_REGEX.test(value)) {
        showError(toast, 'Invalid Date Format', '', 'Please enter a valid date format (MM/YYYY)');

        return;
      }
    }
    setDocumentDetails((prevDetails) => ({
      ...prevDetails,
      [field]: value,
    }));
  };

  const handleSelectChange = (selectedOption, field) => {
    if (field === 'document_sub_type') {
      setDocumentDetails((prevDetails) => ({
        ...prevDetails,
        document_sub_type: selectedOption.value,
      }));
    }
  };

  const documentOptions = document_sub_type?.map((subType) => ({
    value: subType,
    label: snakeToTitleCase(subType),
  }));

  const documentCategories = useMemo(() => {
    return documentInfo
      ?.find((doc) => doc.value === document_type)
      ?.subtypes?.find((doc) => doc.value === documentDetails?.document_sub_type)?.categories;
  }, [documentInfo, document_type, documentDetails?.document_sub_type]);

  useEffect(() => {
    const updatedDocuments = selectedFiles.map((file, idx) => ({
      document_name: file?.data?.identifier,
      ...documentDetails,
      metadata: is_document_required ? (idx === 0 ? 'FRONT' : 'BACK') : `${idx + 1}`,
    }));

    updateAnswer({ ...answer, document: updatedDocuments });
  }, [selectedFiles, documentDetails, is_document_required, is_front_back_required]);

  return (
    <FormControl>
      <FormLabel fontWeight='semibold' whiteSpace='pre-wrap'>
        {`${questionNo}. ${formatString(prompt)}`}
      </FormLabel>
      {is_document_required ? (
        <>
          <Select
            placeholder='Select Document Type'
            options={documentOptions}
            onChange={(selectedOption) => handleSelectChange(selectedOption, 'document_sub_type')}
          />
          {documentDetails?.document_sub_type && documentCategories?.length > 0 && (
            <Flex direction='column' gap={1} mt={2}>
              <Text fontSize='sm' color='gray.600'>
                Upload one of the following documents:
              </Text>
              <UnorderedList pl={4} fontSize='sm' color='gray.600'>
                {documentCategories?.map((category, index) => (
                  <ListItem key={index}>{category}</ListItem>
                ))}
              </UnorderedList>
            </Flex>
          )}
          {document_type === 'identity_card' && (
            <>
              <FormLabel mt={3} fontSize='sm' color='gray.600'>
                Document Expiry Date (MM/YYYY)
              </FormLabel>
              <Input
                type='text'
                placeholder='Enter Document Expiry Date'
                value={documentDetails.expiry_date}
                onChange={(e) => handleInputChange(e, 'expiry_date')}
                maxLength={7}
                minLength={7}
              />
            </>
          )}
          <FormLabel mt={3} fontSize='sm' color='gray.600'>
            {is_front_back_required ? 'Upload both front and back side of the document' : 'Upload the Document'}
          </FormLabel>
          <FileUploader
            numDocuments={is_front_back_required ? 2 : 1}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            acceptedFormats={DOCUMENT_ACCEPTED_FORMATS.join(', ')}
            id={id}
          />
          <FormLabel fontSize={'sm'} color={'gray.600'} mt={3}>
            Additional Note
          </FormLabel>
          <Textarea placeholder='Add any remarks...' size='md' onChange={(e) => handleTextareaChange(e, 'comment')} />
        </>
      ) : (
        <>
          <Textarea
            placeholder='Enter Your Response Here'
            size='md'
            value={answer?.notes}
            onChange={(e) => handleTextareaChange(e, 'notes')}
          />
          <FormLabel mt={3} fontSize='sm' color='gray.600'>
            Additional Attachment (if any)
          </FormLabel>
          <FileUploader
            maxDocuments={5}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            acceptedFormats={`${INPUT_FILE_FORMATS.PNG}, ${INPUT_FILE_FORMATS.JPEG}, ${INPUT_FILE_FORMATS.JPG}, ${INPUT_FILE_FORMATS.PDF}, ${INPUT_FILE_FORMATS.BMP}`}
            id={id}
          />
        </>
      )}
    </FormControl>
  );
};

export default KycRfiResponse;
